import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";

const EMPTY = { name: "", code: "", contact: "", address: "" };

export default function CustomersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = user?.role === "admin";

  const fetchAll = () => api.get("/customers").then((r) => setRows(r.data));
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (row) => { setForm({ name: row.name, code: row.code, contact: row.contact, address: row.address }); setEditing(row.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/customers", form);
      else await api.patch(`/customers/${editing}`, form);
      toast.success("Saved");
      close();
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        eyebrow="Catalog"
        title="Customers."
        action={isAdmin && <button onClick={openNew} className="mj-btn mj-btn-primary" data-testid="new-customer-btn"><Plus size={14}/> New Customer</button>}
      />
      <div className="p-6 md:p-10 space-y-6">
        {editing && (
          <form onSubmit={submit} className="mj-card grid md:grid-cols-2 gap-4 items-end" data-testid="customer-form">
            <div className="md:col-span-2 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">{editing === "new" ? "New customer" : "Edit customer"}</h3>
              <button type="button" onClick={close} className="mj-btn mj-btn-ghost"><X size={14}/> Cancel</button>
            </div>
            <div><label className="data-label block mb-2">Name</label><input required className="mj-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="customer-name-input"/></div>
            <div><label className="data-label block mb-2">Code</label><input required className="mj-input font-mono-id" value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} data-testid="customer-code-input"/></div>
            <div><label className="data-label block mb-2">Contact</label><input className="mj-input" value={form.contact} onChange={(e)=>setForm({...form, contact:e.target.value})} data-testid="customer-contact-input"/></div>
            <div><label className="data-label block mb-2">Address</label><input className="mj-input" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} data-testid="customer-address-input"/></div>
            <div className="md:col-span-2"><button type="submit" className="mj-btn mj-btn-primary" data-testid="customer-submit-btn">Save</button></div>
          </form>
        )}

        <div className="mj-card">
          <table className="mj-table" data-testid="customers-table">
            <thead><tr><th>Name</th><th>Code</th><th>Contact</th><th>Address</th>{isAdmin && <th></th>}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-zinc-500">No customers yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="font-mono-id">{r.code}</td>
                  <td>{r.contact}</td>
                  <td className="text-[var(--mj-ink-muted)]">{r.address}</td>
                  {isAdmin && (
                    <td className="text-right">
                      <button onClick={() => openEdit(r)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px"}} data-testid={`edit-customer-${r.id}`}><Pencil size={14}/></button>
                      <button onClick={() => remove(r.id)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px", color: "var(--mj-red)"}} data-testid={`del-customer-${r.id}`}><Trash2 size={14}/></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
